/* Copyright (c) 2017 Backpack Health LLC */
/* eslint-disable react/display-name */

import * as _ from 'lodash';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import store from '../app/redux/reduxStore_authorizedApp';

import { selectors as listSelectors } from '../app/redux/ducks/lists/lists';
import { HealthEntryDateSummaryDate, HealthEntryListDate } from '../app/localization/LocalizedDate';
import Frequency from '../models/Frequency';
import HealthEntryCategoryNames from '../constants/HealthEntryCategoryNames';
import ShareCardIncludedTypeNames from '../constants/ShareCardIncludedTypeNames';
import MedliAmountText from '../app/components/MedliAmountText';
import { LocalizedDiagnosis } from '../app/localization/LocalizedDiagnosis';
import GeneVariantResultText from '../app/components/GeneVariantResultText';
import GeneAttributesListResultText, { areGeneAttributesDefined } from '../app/components/GeneAttributesListResultText';
import COLORS from '../app/styles/Colors';
import { AttributeState } from './ShareCardTemplateModel';

function formatAmount(healthEntry, languageCode) {
  return (
    <MedliAmountText
      amount={healthEntry.amount.get()}
      description={healthEntry.description}
      languageCode={languageCode}
      displayNAUnit={false}
    />
  );
}

export function formatFrequency(frequencyData) {
  const state = store.getState();
  const currentFrequency = listSelectors.getFrequencyFromId(frequencyData.id, state);
  const frequencyPhraseKeyData = Frequency.getPhraseData(currentFrequency, frequencyData);
  return <FormattedMessage {...frequencyPhraseKeyData} />;
}

export function formatMissingDate(healthEntry) {
  if (healthEntry.current.hasCurrent()) {
    if (healthEntry.current.isCurrent()) {
      return <FormattedMessage id="ui-data.healthentryperiod.current.text" />;
    } else {
      return <FormattedMessage id="ui-data.healthentryperiod.past.text" />;
    }
  } else {
    return <FormattedMessage id="ui-data.date.not-specified.text" />;
  }
}

export function formatAmountAndFrequency(healthEntry, languageCode) {
  const state = store.getState();
  const hasAmount = healthEntry.amount && healthEntry.amount.hasAmount();
  const hasFrequency = healthEntry.frequency && healthEntry.frequency.hasFrequency();

  if (hasAmount && hasFrequency) {
    return (
      <span>
        {formatAmount(healthEntry, languageCode)}, <span style={{ textTransform: 'lowercase' }}>{formatFrequency(healthEntry.frequency.get(), state)}</span>
      </span>
    );
  } else if (hasAmount) {
    return formatAmount(healthEntry, languageCode);
  } else if (hasFrequency) {
    return formatFrequency(healthEntry.frequency.get());
  } else {
    return null;
  }
}

export function formatSubType(healthEntry) {
  return healthEntry.subType ? <FormattedMessage id={healthEntry.subType.getPhraseKey()} values={{ digit1: 1 }} /> : null;
}

export function formatDateField(healthEntry) {
  return (healthEntry.startDate.hasDate() || healthEntry.endDate.hasDate()) ? (
    <HealthEntryDateSummaryDate
      startDateData={healthEntry.startDate.data()}
      endDateData={healthEntry.endDate.data()}
      isCurrent={healthEntry.current.isCurrent()}
      isPeriod={healthEntry.period.isPeriod()}
    />
  ) : formatMissingDate(healthEntry);
}

export function listDateFormatMultipleOccurrence(healthEntry, languageCode) {
  return (
    <HealthEntryListDate
      startDateData={healthEntry.dateUtil.mostRecentDate()}
      endDateData={{ date: '', precision: '' }}
      isCurrent={false}
      isPeriod={false}
      isOnlyPast={true}
      languageCode={languageCode}
    />
  );
}

export function listDateFormatSingleOccurrence(healthEntry, languageCode) {
  return (
    <HealthEntryListDate
      startDateData={healthEntry.startDate.data()}
      endDateData={healthEntry.endDate.data()}
      isCurrent={healthEntry.current.isCurrent()}
      isPeriod={healthEntry.period.isPeriod()}
      isOnlyPast={healthEntry.dateUtil.isOnlyPast()}
      languageCode={languageCode}
    />
  );
}

export function listDateFormatHeValue(heValue, languageCode) {
  return (
    <HealthEntryListDate
      startDateData={heValue.date.data()}
      endDateData={{ date: '', precision: '' }}
      isCurrent={false}
      isPeriod={false}
      isOnlyPast={true}
      languageCode={languageCode}
    />
  );
}

export function formatReaction(healthEntry, languageCode) {
  return healthEntry.reaction && healthEntry.reaction.hasReaction() ? <FormattedMessage id="healthEntry.reactions.title.text" values={{ string1: healthEntry.reaction.formattedNames(languageCode) }} /> : null;
}

export function formatComments(healthEntry, shareCardDefinition) {
  if (shareCardDefinition && shareCardDefinition.comment.health_entry === AttributeState.excluded) { return null; }

  if (!healthEntry.comments.hasSharedComments()) { return null; }

  let comments = [];
  if (healthEntry.values && healthEntry.values.hasValues()) {
    comments = healthEntry.values.mostRecent().comments.getSharedComments();
  } else {
    comments = healthEntry.comments.getSharedComments();
  }

  const additionalStyle = { marginTop: 8, paddingTop: 8, borderTop: `1px solid ${COLORS.gsLight}` };
  return comments.map((c, i) => {
    return (
      <div key={c.id}style={i > 0 ? additionalStyle : null}>{c.content}</div>
    );
  })
}

export function formatName(healthEntry, languageCode) {
  return healthEntry.medliTerm.needsLocalization() ? (
    <FormattedMessage {...healthEntry.medliTerm.localizationData(languageCode)} />
  ) : healthEntry.medliTerm.formattedTermName(languageCode);
}

export function formatNameText(healthEntry, languageCode, intl) {
  const phraseData = healthEntry.medliTerm.localizationData(languageCode);

  return healthEntry.medliTerm.needsLocalization() ? (
    intl.formatMessage({ id: phraseData.id }, phraseData.values)
  ) : healthEntry.medliTerm.formattedTermName(languageCode);
}

export function formatNameWithAnatomy(healthEntry, languageCode) {
  const name = healthEntry.medliTerm.needsLocalization() ? (
    <FormattedMessage {...healthEntry.medliTerm.localizationData(languageCode)} />
  ) : healthEntry.medliTerm.formattedTermName(languageCode);

  const anatomy = healthEntry.anatomy && healthEntry.anatomy.hasAnatomy() ? (
    <span> - {healthEntry.anatomy.formattedNames(languageCode)}</span>
  ) : null;

  return <span>{name}{anatomy}</span>;
}

export function formatStoppingReason(healthEntry, languageCode) {
  return healthEntry.stoppingReason.hasStoppingReason() ? <FormattedMessage id="healthEntry.stopped.title.text" values={{ string1: healthEntry.stoppingReason.text(languageCode) }} /> : null;
}

export function formatNameWithLatestValue(healthEntry, showValueInfo, languageCode) {
  const name = healthEntry.medliTerm.needsLocalization() ? (
    <FormattedMessage {...healthEntry.medliTerm.localizationData(languageCode)} />
  ) : healthEntry.medliTerm.formattedTermName(languageCode);

  const anatomy = healthEntry.anatomy && healthEntry.anatomy.hasAnatomy() ? (
    <span> - {healthEntry.anatomy.formattedNames(languageCode)}</span>
  ) : null;

  const latestValue = healthEntry.values && healthEntry.values.hasValues() ? healthEntry.values.mostRecent() : null;
  const valueAmountText = (latestValue && latestValue.amount) ? (
    <span>: <MedliAmountText amount={latestValue.amount.get()} description={latestValue.description.get()} languageCode={languageCode} /></span>
  ) : null;

  const valueGeneVariantResultText = (latestValue && latestValue.geneVariantResult) ? (
    <span>: <GeneVariantResultText geneVariantResult={latestValue.geneVariantResult.get()} languageCode={languageCode} /></span>
  ) : null;

  return <span>{name}{anatomy}{valueAmountText}{valueGeneVariantResultText}</span>;
}

export function formatGeneAttributesListResult(healthEntry, languageCode) {
  const { geneVariantResult } = healthEntry.values.mostRecent();

  if (!areGeneAttributesDefined(geneVariantResult)) return null;

  return (
    <GeneAttributesListResultText
      geneVariantResult={geneVariantResult}
      languageCode={languageCode}
    />
  );
}

const fullSummaryList = {
  all: {
    [HealthEntryCategoryNames.lab]: (healthEntry, languageCode) => _.compact([
      formatName(healthEntry, languageCode),
      <FormattedMessage key={HealthEntryCategoryNames.lab} id="healthEntry.occurrenceCount.message.text" values={{ digit1: healthEntry.values.count() }} />,
    ]),

    [HealthEntryCategoryNames.measurement]: (healthEntry, languageCode) => _.compact([
      formatName(healthEntry, languageCode),
      <FormattedMessage key={HealthEntryCategoryNames.measurement} id="healthEntry.occurrenceCount.message.text" values={{ digit1: healthEntry.values.count() }} />,
    ]),

    [HealthEntryCategoryNames.geneticResult]: (healthEntry, languageCode) => _.compact([
      formatName(healthEntry, languageCode),
      <FormattedMessage key={HealthEntryCategoryNames.geneticResult} id="healthEntry.occurrenceCount.message.text" values={{ digit1: healthEntry.values.count() }} />,
    ]),
  },
  latest: {
    [HealthEntryCategoryNames.allergy]: (healthEntry, languageCode, shareCardDefinition) => _.compact([
      formatName(healthEntry, languageCode),
      <FormattedMessage key={HealthEntryCategoryNames.allergy} id={healthEntry.subType.getPhraseKey()} values={{ digit1: 1 }} />,
      formatReaction(healthEntry, languageCode),
      listDateFormatSingleOccurrence(healthEntry, languageCode),
      healthEntry.diagnosis.getValue() ? <LocalizedDiagnosis healthEntry={healthEntry} /> : null,
      formatComments(healthEntry, shareCardDefinition),
    ]),

    [HealthEntryCategoryNames.condition]: (healthEntry, languageCode, shareCardDefinition) => _.compact([
      formatNameWithAnatomy(healthEntry, languageCode),
      listDateFormatSingleOccurrence(healthEntry, languageCode),
      healthEntry.diagnosis.getValue() ? <LocalizedDiagnosis healthEntry={healthEntry} /> : null,
      formatComments(healthEntry, shareCardDefinition),
    ]),

    [HealthEntryCategoryNames.medication]: (healthEntry, languageCode, shareCardDefinition) => _.compact([
      formatName(healthEntry, languageCode),
      formatAmountAndFrequency(healthEntry, languageCode),
      listDateFormatSingleOccurrence(healthEntry, languageCode),
      formatStoppingReason(healthEntry, languageCode),
      formatReaction(healthEntry, languageCode),
      formatComments(healthEntry, shareCardDefinition),
    ]),

    [HealthEntryCategoryNames.otherTreatment]: (healthEntry, languageCode, shareCardDefinition) => _.compact([
      formatNameWithAnatomy(healthEntry, languageCode),
      formatAmountAndFrequency(healthEntry, languageCode),
      listDateFormatSingleOccurrence(healthEntry, languageCode),
      formatStoppingReason(healthEntry, languageCode),
      formatReaction(healthEntry, languageCode),
      formatComments(healthEntry, shareCardDefinition),
    ]),

    [HealthEntryCategoryNames.procedure]: (healthEntry, languageCode, shareCardDefinition) => _.compact([
      formatNameWithAnatomy(healthEntry, languageCode),
      listDateFormatSingleOccurrence(healthEntry, languageCode),
      formatComments(healthEntry, shareCardDefinition),
    ]),

    [HealthEntryCategoryNames.vaccination]: (healthEntry, languageCode, shareCardDefinition) => _.compact([
      formatName(healthEntry, languageCode),
      listDateFormatSingleOccurrence(healthEntry, languageCode),
      formatComments(healthEntry, shareCardDefinition),
    ]),

    [HealthEntryCategoryNames.lab]: (healthEntry, languageCode, shareCardDefinition) => _.compact([
      formatName(healthEntry, languageCode),
      listDateFormatMultipleOccurrence(healthEntry, languageCode),
      formatComments(healthEntry, shareCardDefinition),
    ]),

    [HealthEntryCategoryNames.measurement]: (healthEntry, languageCode, shareCardDefinition) => _.compact([
      formatName(healthEntry, languageCode),
      listDateFormatMultipleOccurrence(healthEntry, languageCode),
      formatComments(healthEntry, shareCardDefinition),
    ]),

    [HealthEntryCategoryNames.geneticResult]: (healthEntry, languageCode, shareCardDefinition) => _.compact([
      formatName(healthEntry, languageCode),
      formatGeneAttributesListResult(healthEntry, languageCode),
      listDateFormatMultipleOccurrence(healthEntry, languageCode),
      formatComments(healthEntry, shareCardDefinition),
    ]),

  },

};

function healthEntryTextFormatting(healthEntry, includedValues = ShareCardIncludedTypeNames.latest, shareCardDefinition) {
  return {

    amountAndFrequency: () => formatAmountAndFrequency(healthEntry),

    subType: () => formatSubType(healthEntry),

    dateField: () => formatDateField(healthEntry),

    summary: () => {
      let amtFreqText = '';
      const subTypeText = formatSubType(healthEntry);
      let dateText = '';
      switch (healthEntry.category) {
        case HealthEntryCategoryNames.lab:
        case HealthEntryCategoryNames.measurement:
        case HealthEntryCategoryNames.geneticResult:
          if (formatAmountAndFrequency(healthEntry)) {
            amtFreqText = formatAmountAndFrequency(healthEntry);
            dateText = listDateFormatMultipleOccurrence(healthEntry);
          }
          break;
        default:
          amtFreqText = formatAmountAndFrequency(healthEntry);
          dateText = listDateFormatSingleOccurrence(healthEntry);
          break;
      }

      const text = _.compact([
        amtFreqText,
        amtFreqText ? ' - ' : null,
        subTypeText,
        subTypeText ? ' - ' : null,
        dateText,
      ]);

      return text.length === 0 ? null : text.map((a, index) => <span key={index}>{a}</span>);
    },

    fullSummaryList: (languageCode) => fullSummaryList[includedValues][healthEntry.category](healthEntry, languageCode, shareCardDefinition),

    dateContext: () => {

    },

    name: (languageCode) => (
      healthEntry.medliTerm.needsLocalization() ? (
        <FormattedMessage {...healthEntry.medliTerm.localizationData(languageCode)} />
      ) : healthEntry.medliTerm.formattedTermName(languageCode)
    ),

    nameWithAnatomy: (languageCode) => formatNameWithAnatomy(healthEntry, languageCode),
    nameWithLatestValue: (languageCode, showValueInfo) => formatNameWithLatestValue(healthEntry, showValueInfo, languageCode),
  };
}

export default healthEntryTextFormatting;
